import { GQL_ID } from '@@types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useModalStore } from '@features/modal'
import { useManagePaymentMethods } from '@features/payment/hooks/useManagePaymentMethods'

import { useAnalytics } from '@shared/lib/analytics'
import Button from '@shared/ui/button'
import { useToastStore } from '@shared/ui/toast/useToastStore'

import Item from './Item'

import styles from './styles.module.scss'

type PaymentModalProps = {
  onChoose: (id: GQL_ID) => void;
  current: GQL_ID | null;
  hideNewMethods?: boolean;
};

export default function PaymentModal ({ onChoose, current, hideNewMethods = false }: PaymentModalProps) {
  const { paymentMethods, handleDefault, defaultCard } =
    useManagePaymentMethods()
  const { closeAllModals } = useModalStore()
  const { t: tc } = useTranslation('common')
  const { t: te } = useTranslation('errors')
  const { trackEvent } = useAnalytics()
  const addToast = useToastStore((state) => state.addToast)
  const [ selected, setSelected ] = useState<string | null>(
    current === 'new-card' || current === 'new-sbp' ? current : null,
  )
  const [ isProcessing, setIsProcessing ] = useState(false)

  const onSelect = (id: string | null) => {
    setSelected(id)
  }

  const handleChoose = async () => {
    const paymentId = selected || defaultCard?.id
    
    if (!paymentId) {
      addToast({
        message: te('payment_method_required') || 'Выберите способ оплаты',
        variant: 'error'
      })
      return
    }
    
    setIsProcessing(true)
    
    try {
      // Отслеживаем выбор способа оплаты
      trackEvent('select_payment_method', {
        payment_type: selected === 'new-card' ? 'new_card' : 
          selected === 'new-sbp' ? 'new_sbp' : 'saved_card'
      })
      
      onChoose(paymentId)
      closeAllModals()
    }
    catch (error) {
      console.error('[PaymentModal] Error selecting payment:', error)
      addToast({
        message: te('payment_selection_error') || 'Ошибка при выборе способа оплаты',
        variant: 'error'
      })
    }
    finally {
      setIsProcessing(false)
    }
  }

  const handleItemSelect = async (id: string) => {
    try {
      setSelected(null)
      await handleDefault(id)
    }
    catch (error) {
      console.error('[PaymentModal] Error setting default card:', error)
      addToast({
        message: te('payment_default_error') || 'Ошибка при выборе карты',
        variant: 'error'
      })
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.root__cards}>
        {!hideNewMethods && (
          <>
            <Item
              key="new-card"
              data={null}
              type="new-card"
              isSelected={selected === 'new-card' || (!paymentMethods.length && selected !== 'new-sbp')}
              onSelect={() => onSelect('new-card')}
            />
            <Item
              key="new-sbp"
              data={null}
              type="new-sbp"
              isSelected={selected === 'new-sbp'}
              onSelect={() => onSelect('new-sbp')}
            />
          </>
        )}
        {paymentMethods.map((el) => (
          <Item
            key={el?.id}
            data={el}
            isSelected={defaultCard?.id === el?.id && selected !== 'new-card' && selected !== 'new-sbp'}
            onSelect={() => handleItemSelect(el?.id as string)}
          />
        ))}
      </div>
      <Button
        onClick={handleChoose}
        className={styles.root__button}
        size="large"
        disabled={isProcessing}
        loading={isProcessing}
      >
        {tc('choose')}
      </Button>
    </div>
  )
}